import type { FormField } from '@/models/form';

export const FIELD_REGEX = /@([a-z0-9]{6})/g;
export const TEMP_FIELD_REGEX = /@(TEMPID-[a-z0-9]{8})/g;
export const FIELD_VARIABLE_REGEX = /^[A-Z]+(?:_[A-Z]+)*$/;

/**
 * Helper function to resolve a field reference.
 * @param {FormField[]} fields - Array of field objects.
 * @param {string} identifier - Identifier to resolve.
 * @param {keyof FormField} fieldKey - Key to find the field (either 'identifier' or 'internalId').
 * @returns {string} - Resolved label or the original identifier if not found.
 */
const resolveReference = (fields: FormField[], identifier: string, fieldKey: keyof FormField): string => {
  const field = fields.find(f => f[fieldKey] === identifier);
  return field ? '@' + resolveFieldReferences(fields, field.label) : '@' + identifier;
};

/**
 * Resolves field references in a label based on given fields.
 * @param {FormField[]} fields - Array of field objects.
 * @param {string} label - Label to resolve.
 * @returns {string} - Label with resolved field references.
 */
const resolveFieldReferences = (fields: FormField[], label: string): string => {
  return label.split('\n').map(line => {
    return line.split(' ').map(piece => {
      piece = piece.replace(FIELD_REGEX, (_, identifier) => {
        return resolveReference(fields, identifier, 'identifier');
      });
      piece = piece.replace(TEMP_FIELD_REGEX, (_, identifier) => {
        return resolveReference(fields, identifier, 'internalId');
      });
      return piece;
    }).join(' ');
  }).join('\n');
};
  
/**
   * Converts a value with field references to HTML.
   * @param {FormField[]} fields - Array of field objects.
   * @param {string} value - The value to convert to HTML.
   * @returns {string | undefined} - The HTML string or undefined if the value is empty.
   */
export const valueToHtml = (fields: FormField[] = [], value?: string): string | undefined => {
  if (!value) return;
  
  return value.split('\n').map(line => {
    return line.split(' ').map(piece => {
      let replaced = piece.replace(FIELD_REGEX, (match, identifier) => {
        const field = fields.find(f => f.identifier === identifier);
        if (field) {
          const resolved = resolveFieldReferences(fields, field.label);
          return `<span class="mention">@${resolved}</span>${getTrailingCharacter(piece)}`;
        }
        return match;
      });
  
      replaced = replaced.replace(TEMP_FIELD_REGEX, (match, identifier) => {
        const field = fields.find(f => f.internalId === identifier);
        if (field) {
          const resolved = resolveFieldReferences(fields, field.label);
          return `<span class="mention">@${resolved}</span>`;
        }
        return match;
      });
  
      return replaced;
    }).join(' ');
  }).join('\n');
};
  
/**
   * Helper function to get the trailing character if it's a comma or period.
   * @param {string} piece - The piece of text to check.
   * @returns {string} - The trailing character if found, otherwise an empty string.
   */
const getTrailingCharacter = (piece: string): string => {
  if (piece.endsWith(',')) return ',';
  if (piece.endsWith('.')) return '.';
  return '';
};
